import { useSuspenseQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import {
    type Control,
    type FieldPath,
    type FieldValues,
    type Path,
    type PathValue,
    type UseFormReturn,
    useController,
} from 'react-hook-form';

import type * as analytics from '@gitbook/app-analytics/client';
import { Icon } from '@gitbook/spine-icons';
import {
    Button,
    Description,
    DialogRoot,
    FieldGroup,
    Group,
    Label,
    useOverlayTriggerState,
} from '@gitbook/spine-react';

import { apiQuery } from '../../state';
import { OpenAPIEmptyState } from './OpenAPIEmptyState';
import { OpenAPISpecCreateDialog } from './OpenAPISpecCreateDialog';
import { OpenAPISpecSelector } from './OpenAPISpecSelector';

export function OpenAPISpecPicker<TFieldValues extends FieldValues>(props: {
    form: UseFormReturn<TFieldValues>;
    name: FieldPath<TFieldValues>;
    organizationId: string;
    analyticsContext: analytics.OpenAPIAnalyticsCreateContext;
}) {
    const { organizationId, form, name, analyticsContext } = props;
    const { data } = useSuspenseQuery(
        apiQuery('listOpenAPISpecs', {
            organizationId,
            limit: 100,
        })
    );

    // Auto-select the only spec if there is only one
    const autoSelectedSpecSlug = data.items.length > 1 ? data.items[0].slug : null;
    const { setValue } = form;
    useEffect(() => {
        if (autoSelectedSpecSlug) {
            setValue(
                name,
                autoSelectedSpecSlug as unknown as PathValue<TFieldValues, Path<TFieldValues>>,
                {
                    shouldDirty: true,
                }
            );
        }
    }, [name, autoSelectedSpecSlug, setValue]);

    const addOpenAPISpecTrigger = useOverlayTriggerState();

    return (
        <>
            {data.items.length === 0 ? (
                <OpenAPIEmptyState
                    addOpenAPISpecTrigger={addOpenAPISpecTrigger}
                    organizationId={organizationId}
                    className="!max-w-full border"
                    analyticsContext={analyticsContext}
                />
            ) : (
                <OpenAPISpecPickerField
                    name={name}
                    organizationId={organizationId}
                    control={form.control}
                    analyticsContext={analyticsContext}
                />
            )}
            <DialogRoot {...addOpenAPISpecTrigger.rootProps}>
                <OpenAPISpecCreateDialog
                    organizationId={organizationId}
                    onCreated={(openAPISpec) => {
                        setValue(
                            name,
                            openAPISpec.slug as unknown as PathValue<
                                TFieldValues,
                                Path<TFieldValues>
                            >,
                            { shouldDirty: true }
                        );
                    }}
                    analyticsContext={analyticsContext}
                />
            </DialogRoot>
        </>
    );
}

function OpenAPISpecPickerField<TFieldValues extends FieldValues>(props: {
    organizationId: string;
    control: Control<TFieldValues>;
    name: FieldPath<TFieldValues>;
    analyticsContext: analytics.OpenAPIAnalyticsCreateContext;
}) {
    const { organizationId, control, name, analyticsContext } = props;
    const { field } = useController({ control, name });
    return (
        <>
            <FieldGroup orientation="horizontal">
                <Group>
                    <Label>OpenAPI Specification</Label>
                    <Description>
                        Content will be automatically updated when this specification is updated.
                    </Description>
                </Group>
                <OpenAPISpecSelector
                    triggerRef={field.ref}
                    organizationId={organizationId}
                    onChange={(spec) => field.onChange(spec.slug)}
                    analyticsContext={analyticsContext}
                >
                    {({ triggerProps }) => (
                        <div className="flex">
                            <Button
                                {...triggerProps}
                                kind="secondary"
                                trailingIcon={Icon.ChevronDown}
                                label={field.value ?? 'Select specification'}
                                onBlur={field.onBlur}
                                disabled={field.disabled}
                            />
                        </div>
                    )}
                </OpenAPISpecSelector>
            </FieldGroup>
        </>
    );
}
